import React from 'react';

import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';

import { GridColDef } from '@mui/x-data-grid';

import { FDataGrid } from '../grid';

import {
  IObjectiveImpactRow,
  IObjectiveImpactTargetSetOption,
  IObjectiveImpactWorkbench,
} from './simulation.models';


type Props = {
  workbench: IObjectiveImpactWorkbench | null;

  loading?: boolean;

  onTargetSetChange: (
    targetset: IObjectiveImpactTargetSetOption,
  ) => void | Promise<void>;

  onRefresh?: () => void | Promise<void>;
};


class SimulationObjectiveImpactPanel extends React.PureComponent<Props> {

  private formatNumber = (
    value?: number | null,
    maximumFractionDigits = 2,
  ): string => {

    if (value === null || value === undefined) {
      return '—';
    }

    return Number(value).toLocaleString(undefined, {
      maximumFractionDigits,
    });
  };


  private formatCompact = (
    value?: number | null,
  ): string => {

    if (value === null || value === undefined) {
      return '—';
    }

    const n = Number(value);

    if (Math.abs(n) >= 1_000_000_000) {
      return `${(n / 1_000_000_000).toFixed(2)} B`;
    }

    if (Math.abs(n) >= 1_000_000) {
      return `${(n / 1_000_000).toFixed(2)} M`;
    }

    if (Math.abs(n) >= 1_000) {
      return `${(n / 1_000).toFixed(2)} K`;
    }

    return this.formatNumber(n);
  };


  private statusChip = (
    status?: string,
    ismet?: boolean,
  ) => {

    const code =
      String(status || '').toUpperCase();


    if (code === 'NO_DATA') {
      return (
        <Chip
          size="small"
          label="NO DATA"
          variant="outlined"
        />
      );
    }


    if (ismet) {
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={
            code === 'ON_TARGET'
              ? 'ON TARGET'
              : code === 'WITHIN_RANGE'
                ? 'WITHIN RANGE'
                : 'ACHIEVED'
          }
        />
      );
    }


    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={code.replaceAll('_', ' ') || 'REVIEW'}
      />
    );
  };


  private targetDisplay = (
    row: IObjectiveImpactRow,
  ): string => {

    switch (
    String(row.objectivetypecode || '').toUpperCase()
    ) {

      case 'MIN':
        return `≥ ${this.formatCompact(row.targetvalue)}`;

      case 'MAX':
        return `≤ ${this.formatCompact(row.targetvalue)}`;

      case 'RNG':
        return `${this.formatCompact(row.minimumvalue)} – ${this.formatCompact(row.maximumvalue)}`;

      case 'TGT':
      default:
        return this.formatCompact(row.targetvalue);
    }
  };


  private columns: GridColDef<IObjectiveImpactRow>[] = [

    {
      field: 'objectivecode',
      headerName: 'Objective',
      width: 165,
    },

    {
      field: 'objectivename',
      headerName: 'Name',
      minWidth: 230,
      flex: 1,
    },

    {
      field: 'objectivetypecode',
      headerName: 'Rule',
      width: 95,
    },

    {
      field: 'priority',
      headerName: 'Priority',
      width: 95,
      type: 'number',
    },

    {
      field: 'weight',
      headerName: 'Weight',
      width: 90,
      type: 'number',
    },

    {
      field: 'target',
      headerName: 'Governed Target',
      width: 165,

      valueGetter: (_value: any, row: IObjectiveImpactRow) =>
        this.targetDisplay(row),
    },

    {
      field: 'actualvalue',
      headerName: 'Simulation',
      width: 150,

      valueFormatter: (value: any) =>
        this.formatCompact(value),
    },

    {
      field: 'gapvalue',
      headerName: 'Remaining Gap',
      width: 145,

      valueFormatter: (value: any) =>
        this.formatCompact(value),
    },

    {
      field: 'achievementpercent',
      headerName: 'Achievement',
      width: 130,

      valueFormatter: (value: any) =>
        value == null
          ? '—'
          : `${Number(value).toFixed(2)}%`,
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 155,

      renderCell: (params: any) =>
        this.statusChip(
          params.row.status,
          params.row.ismet,
        ),
    },

    {
      field: 'recommendation',
      headerName: 'Management Interpretation',
      minWidth: 310,
      flex: 1.25,
    },
  ];


  private getRowId = (
    row: IObjectiveImpactRow,
  ) =>
    row.id ??
    row.impacttargetix ??
    row.impacttargetid ??
    row.objectiveid ??
    row.objectivecode;


  render() {

    const w = this.props.workbench;

    const summary = w?.summary;

    const rows = w?.objectives || [];

    const targetsets = w?.targetsets || [];

    const selectedTargetSetId =
      w?.targetset?.targetsetid ?? '';


    return (
      <Stack spacing={2}>

        {this.props.loading && (
          <Box>
            <LinearProgress />
          </Box>
        )}


        <Card elevation={1}>
          <CardContent>

            <Stack spacing={2}>

              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800 }}
                >
                  Objective Impact
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Compare the current Simulation Scenario against
                  a governed set of business objectives. This is
                  a read-only assessment and does not change the
                  Scenario.
                </Typography>
              </Box>


              <Grid container spacing={2}>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"

                    label="Governed Target Set"

                    value={selectedTargetSetId}

                    disabled={
                      this.props.loading ||
                      targetsets.length === 0
                    }

                    onChange={(event) => {

                      const selected =
                        targetsets.find(
                          (x) =>
                            x.rid ===
                            Number(event.target.value),
                        );

                      if (selected) {
                        void this.props.onTargetSetChange(
                          selected,
                        );
                      }
                    }}
                  >
                    {targetsets.map((x) => (
                      <MenuItem
                        key={x.rid}
                        value={x.rid}
                      >
                        {x.shc} — {x.nam}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>


                <Grid size={{ xs: 12, md: 6 }}>
                  {w?.targetset?.validation && (
                    <Alert severity="info">
                      This is a technical validation Target Set,
                      not an approved management policy set.
                    </Alert>
                  )}
                </Grid>

              </Grid>

            </Stack>

          </CardContent>
        </Card>


        {summary?.ready && (
          <Grid container spacing={2}>

            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card elevation={1}>
                <CardContent>
                  <Stack direction="row" spacing={1.5}>
                    <FlagOutlinedIcon color="primary" />

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Objectives
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        {summary.objectivecount}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>


            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card elevation={1}>
                <CardContent>
                  <Stack direction="row" spacing={1.5}>
                    <CheckCircleOutlineIcon color="success" />

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Met
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        {summary.metcount}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>


            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card elevation={1}>
                <CardContent>
                  <Stack direction="row" spacing={1.5}>
                    <ErrorOutlineIcon color="warning" />

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Unmet
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        {summary.unmetcount}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>


            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card elevation={1}>
                <CardContent>
                  <Stack direction="row" spacing={1.5}>
                    <TrackChangesOutlinedIcon color="info" />

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Achievement
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800 }}
                      >
                        {Number(
                          summary.achievementpercent || 0,
                        ).toFixed(2)}
                        %
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>


            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card elevation={1}>
                <CardContent>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    High Priority Unmet
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800 }}
                  >
                    {summary.highpriorityunmetcount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        )}


        {!summary?.ready &&
          !this.props.loading && (
            <Alert severity="info">
              No governed Objective Impact Target Set is currently
              available for this Scenario.
            </Alert>
          )}


        {summary?.ready && (
          <FDataGrid
            title="Objective Impact Assessment"

            subtitle={
              w?.targetset
                ? `Current Simulation performance against ${w.targetset.targetsetname}.`
                : 'Current Simulation performance against governed objectives.'
            }

            rows={rows}

            columns={this.columns}

            loading={this.props.loading}

            height={560}

            showQuickFilter

            refresh={this.props.onRefresh}

            getRowId={this.getRowId}

            pageSizeOptions={[10, 25, 50]}
          />
        )}

      </Stack>
    );
  }
}


export default SimulationObjectiveImpactPanel;